import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/base/WorkCard';
import FilterBar, { type CategoryFilters } from '@/pages/category/components/FilterBar';
import Pagination from '@/pages/category/components/Pagination';
import { CATEGORY_META, PAGE_SIZE } from '@/constants';
import { works } from '@/mocks/works';
import type { WorkType } from '@/types';

const DEFAULT_FILTERS: CategoryFilters = { genre: '全部', region: '全部', year: '全部', sort: 'latest' };

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const workType = (type as WorkType) || 'movie';
  const meta = CATEGORY_META[workType];

  const [filters, setFilters] = useState<CategoryFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, [workType]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const filtered = useMemo(() => {
    if (!meta) return [];
    let list = works.filter((w) => w.type === workType);
    if (filters.genre !== '全部') list = list.filter((w) => w.genres.includes(filters.genre));
    if (filters.region !== '全部') list = list.filter((w) => w.region.includes(filters.region));
    if (filters.year !== '全部') list = list.filter((w) => String(w.year) === filters.year);

    if (filters.sort === 'hot') list = [...list].sort((a, b) => a.rank - b.rank);
    else if (filters.sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    else list = [...list].sort((a, b) => b.year - a.year);
    return list;
  }, [filters, workType, meta]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!meta) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-24 text-center md:px-6">
        <p className="text-lg text-foreground-700">未找到对应的影视分类</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="text-primary-200">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.title }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-4xl">{meta.title}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-primary-100 md:text-base">{meta.desc}</p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <FilterBar value={filters} onChange={setFilters} onReset={() => setFilters(DEFAULT_FILTERS)} />

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
          </p>
        </div>

        {pageItems.length === 0 ? (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 py-20 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400" />
            <p className="mt-3 text-foreground-700">暂无符合条件的影视作品</p>
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
              {pageItems.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-8">
          <span className="text-sm text-foreground-600">相关分类：</span>
          {(['movie', 'tv'] as WorkType[]).map((t) => (
            <Link
              key={t}
              to={`/category/${t}`}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                t === workType
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
              }`}
            >
              {CATEGORY_META[t].title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}